// decision-policy-journal-events.ts — V2-1 (spec V2 §4.1 + bảng §5.1, I1 E6, I6).
//
// Delta journal của nhánh needs_decision:
//  - `decision_question_asked` — MỌI mission, mọi mode: snapshot câu hỏi để
//    journal tự dựng được sổ khi mất interview.json (Codex F3);
//  - `decision_auto_applied` — máy tự áp; `actor` = `delegated:<G0 request id>`,
//    KHÔNG BAO GIỜ là user id (I6);
//  - `decision_auto_skipped` — chỉ khi policy hiệu lực = `auto` mà không áp.
// Mọi chuỗi tự do (summary, nhãn, lý do) qua `SecretRedactor` (pattern +
// known-values HIỆN TẠI do caller truyền) TRÊN TOÀN TEXT rồi mới cắt 4000 ký tự
// (giới hạn §5.4) — Codex F9: cắt trước thì secret vắt qua điểm cắt để lại tiền
// tố mà `MissionStore.commit` (lớp cuối) không còn khớp được. Reducer v1 bỏ
// qua các kind này (`default: break`).
import { join } from "node:path";
import { scanJournal } from "./journal-scan-report.js";
import type { MissionDelta } from "./mission-state-types.js";
import { MISSION_JOURNAL_FILE_NAME } from "./mission-store.js";
import { needsDecisionQuestionTicket } from "./needs-decision-question-ticket-match.js";
import { redactEvidence } from "./retry-policy-evidence.js";
import type { InterviewQuestion } from "./schemas/interview-schema.js";

/** Giới hạn ký tự cho mỗi chuỗi tự do trong payload (spec §5.4). */
export const DECISION_JOURNAL_TEXT_MAX_CHARS = 4000;

/** `secretValues` BẮT BUỘC (Codex F9 vòng 2): caller tự quyết, không có known-values ⇒ `[]` tường minh. */
export function redactDecisionText(text: string, secretValues: readonly string[]): string {
  return redactEvidence(text, secretValues).slice(0, DECISION_JOURNAL_TEXT_MAX_CHARS);
}

function ticketField(question: InterviewQuestion): { ticket_id?: string } {
  const ticketId = needsDecisionQuestionTicket(question);
  return ticketId === undefined ? {} : { ticket_id: ticketId };
}

export function decisionQuestionAskedDelta(
  question: InterviewQuestion,
  mode: "ask" | "auto",
  recommendedOptionId: string | undefined,
  secretValues: readonly string[],
): MissionDelta {
  const labels = question.options ?? [];
  const ids = question.option_ids;
  return {
    kind: "decision_question_asked",
    question_id: question.id,
    ...ticketField(question),
    mode,
    summary: redactDecisionText(question.title, secretValues),
    options: labels.map((label, i) => ({
      ...(ids?.[i] !== undefined ? { id: ids[i] } : {}),
      label: redactDecisionText(label, secretValues),
    })),
    // Chỉ khi trỏ đúng một id của câu (worker cũ / id sai ⇒ vắng).
    ...(recommendedOptionId !== undefined && ids?.includes(recommendedOptionId)
      ? { recommended_option_id: recommendedOptionId }
      : {}),
  };
}

export function decisionAutoAppliedDelta(input: {
  question: InterviewQuestion;
  optionId: string;
  optionLabel: string;
  claims: Record<string, boolean>;
  g0RequestId: string;
  blueprintHash: string;
  /** Thời điểm áp (ISO) — cột "thời điểm" của sổ khi mất interview.json (§4.1 (15)). */
  decidedAt: string;
  /** Codex F9: known-values hiện tại — nhãn là chữ thô của worker, che trước khi cắt. */
  secretValues: readonly string[];
}): MissionDelta {
  return {
    kind: "decision_auto_applied",
    question_id: input.question.id,
    ...ticketField(input.question),
    option_id: input.optionId,
    option_label: redactDecisionText(input.optionLabel, input.secretValues),
    claims: input.claims,
    authority_ref: input.g0RequestId,
    blueprint_hash: input.blueprintHash,
    actor: delegatedActor(input.g0RequestId),
    decided_at: input.decidedAt,
  };
}

export function decisionAutoSkippedDelta(questionId: string, reason: string): MissionDelta {
  return {
    kind: "decision_auto_skipped",
    question_id: questionId,
    // Lý do do controller tự sinh (tên token/lỗi), không phải chữ worker ⇒ không cần known-values.
    reason: redactDecisionText(reason, []),
  };
}

/** I6: nguồn của quyết định máy tự áp — G0 request id, không phải người. */
export function delegatedActor(g0RequestId: string): string {
  return `delegated:${g0RequestId}`;
}

/** Số `decision_auto_applied` đã ghi bền — đếm cho trần auto (§4.1 điều 4). */
export async function countJournaledAutoDecisions(controlRoot: string): Promise<number> {
  const scan = await scanJournal(join(controlRoot, MISSION_JOURNAL_FILE_NAME));
  return scan.records.filter((r) => r.delta.kind === "decision_auto_applied").length;
}
